import React, { useEffect } from "react";
import { Title } from "@jasper/shared/stories/Title/Title";
import { ButtonRadioSelect } from "@jasper/shared/stories/ButtonRadioSelect/ButtonRadioSelect";
import { DentalSvgWithButtons } from "@jasper/shared/stories/DentalSvgWithButtons/DentalSvgWithButtons";
import { Controller, useFormContext } from "react-hook-form";
import { Product } from "@jasper/shared";

interface PartialDentureInfoProps {
  product: Product;
}

export const PartialDentureInfo = ({ product }: PartialDentureInfoProps) => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();
  const formInitialized = React.useRef(false);
  const dentalArchesSvg = [
    {
      label: "Extraites le jour de la pose",
      teeth: product?.removableItem?.[0]?.teethToManufacture ?? [],
      color: "blue",
      description: "La dent/racine sera extraite le jour de la pose",
      key: "to-extract",
      name: "toExtract",
    },
    {
      label: "Dents déjà manquantes",
      teeth: product?.removableItem?.[0]?.partialDenture?.missingTeeth ?? [],
      color: "black",
      description: "La dent est déjà absente en bouche du patient.",
      key: "missing-teeth",
      name: "missingTeeth",
    },
  ];

  useEffect(() => {
    if (
      product?.removableItem?.[0]?.partialDenture &&
      !formInitialized.current
    ) {
      setValue(
        "isReplacement",
        product.removableItem[0].isReplacement.toString()
      );
      setValue("teeths", {
        toExtract: product.removableItem[0].teethToManufacture,
        missingTeeth: product.removableItem[0].partialDenture.missingTeeth,
      });
      formInitialized.current = true;
    }
  }, [product]);

  return (
    <div>
      <Title
        title="Information sur votre prothèse amovible partielle"
        size={3}
        mb="md"
      />
      <Controller
        control={control}
        name="isReplacement"
        rules={{ required: "Veuillez sélectionner une option" }}
        render={({ field }) => (
          <ButtonRadioSelect
            options={[
              { label: "Oui", value: "true" },
              { label: "Non", value: "false" },
            ]}
            selected={field.value}
            setSelected={value => field.onChange(value)}
            label="S'agit-il d'un remplacement de prothèse ?"
          />
        )}
      />
      {errors.isReplacement && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors.isReplacement.message as string}
        </span>
      )}
      <Title
        title="Sélectionnez les dents à inclure dans la PAP"
        size={5}
      />
      <Controller
        control={control}
        name="teeths"
        rules={{
          validate: value =>
            (value?.toExtract?.length ?? 0) > 0 ||
            (value?.missingTeeth?.length ?? 0) > 0 ||
            "Veuillez sélectionner au moins une dent",
        }}
        render={({ field }) => {
          return (
            <DentalSvgWithButtons
              teethChoices={dentalArchesSvg}
              onClick={value =>
                field.onChange(
                  value
                    .map(tooth => ({ [tooth.name]: tooth.teeth }))
                    .reduce((acc, curr) => ({ ...acc, ...curr }), {})
                )
              }
            />
          );
        }}
      />
      {errors.teeths && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors.teeths.message as string}
        </span>
      )}
    </div>
  );
};
